/**
 * Internal Subservice Detail Page
 */
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Divider,
  Button,
  Chip,
  CircularProgress,
  Avatar,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Image as ImageIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { PageHeader, ConfirmDialog } from '../../components/common';
import {
  useGetSubServiceQuery,
  useDeleteInternalSubServiceMutation,
} from '../../store/api/servicesApi';
import type { InternalSubService } from '../../types/service';
import { formatCurrency as formatPrice } from '../../utils/pricing';
import moment from 'moment';
import toast from 'react-hot-toast';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

const InternalSubserviceDetailPage = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [deleteDialog, setDeleteDialog] = useState(false);

  const { data, isLoading, error } = useGetSubServiceQuery(Number(id));
  const [deleteInternalSubService, { isLoading: isDeleting }] = useDeleteInternalSubServiceMutation();

  const internalSubservice = data?.data as unknown as InternalSubService;

  const handleDelete = async () => {
    if (!internalSubservice || !internalSubservice.parent_id) return;

    try {
      await deleteInternalSubService({
        subservice_id: internalSubservice.parent_id,
        id: internalSubservice.id,
      }).unwrap();
      toast.success(t('subservices.toast.internalDeleted'));
      navigate('/internal-subservices');
    } catch (err: any) {
      toast.error(err?.data?.message || t('subservices.toast.internalDeleteFailed'));
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', py: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !internalSubservice) {
    return (
      <Box sx={{ p: 3, textAlign: 'center', py: 10 }}>
        <Typography color="error">{t('subservices.detail.internalNotFound')}</Typography>
        <Button onClick={() => navigate('/internal-subservices')} sx={{ mt: 2 }}>
          {t('subservices.buttons.backToInternal')}
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        title={internalSubservice.name}
        breadcrumbs={[
          { label: t('nav.dashboard'), path: '/' },
          { label: t('nav.internalSubServices'), path: '/internal-subservices' },
          { label: internalSubservice.name },
        ]}
        actionLabel={t('common.edit')}
        actionIcon={<EditIcon />}
        onAction={() => {
          navigate(`/internal-subservices/${internalSubservice.id}/edit`);
        }}
      />

      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/internal-subservices')}
        sx={{ mb: 3 }}
      >
        {t('subservices.buttons.backToInternal')}
      </Button>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            md: '2fr 1fr',
          },
          gap: 3,
        }}
      >
        {/* Main Content */}
        <Box>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
                <Box>
                  <Typography variant="h5" fontWeight={600} gutterBottom>
                    {t('subservices.detail.internalTitle')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('subservices.detail.createdOn', { date: moment(internalSubservice.created_at).format('MMMM DD, YYYY') })}
                  </Typography>
                </Box>
                <Chip
                  label={internalSubservice.is_active ? t('subservices.status.ACTIVE') : t('subservices.status.INACTIVE')}
                  color={internalSubservice.is_active ? 'success' : 'default'}
                />
              </Box>

              <Divider sx={{ my: 3 }} />

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    sm: 'repeat(2, 1fr)',
                  },
                  gap: 3,
                }}
              >
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                    {t('subservices.detail.name')}
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {internalSubservice.name}
                  </Typography>
                </Box>

                {internalSubservice.name_ar && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                      {t('subservices.detail.nameAr')}
                    </Typography>
                    <Typography variant="body1" fontWeight={500} dir="rtl">
                      {internalSubservice.name_ar}
                    </Typography>
                  </Box>
                )}

                <Box>
                  <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                    {t('subservices.detail.service')}
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {internalSubservice.service_name}
                  </Typography>
                </Box>

                {internalSubservice.parent_name && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                      {t('subservices.detail.parentSubservice')}
                    </Typography>
                    <Button
                      variant="text"
                      onClick={() => navigate(`/subservices/${internalSubservice.parent_id}`)}
                      sx={{ p: 0, textTransform: 'none', fontWeight: 500 }}
                    >
                      {internalSubservice.parent_name}
                    </Button>
                  </Box>
                )}

                {internalSubservice.slug && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                      {t('subservices.detail.slug')}
                    </Typography>
                    <Typography variant="body1" fontWeight={500}>
                      {internalSubservice.slug}
                    </Typography>
                  </Box>
                )}

                {internalSubservice.uses_dual_pricing ? (
                  <>
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                        {t('subservices.detail.specialistPrice')}
                      </Typography>
                      <Typography variant="body1" fontWeight={600} color="primary">
                        {formatPrice(internalSubservice.specialist_price)}
                      </Typography>
                    </Box>

                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                        {t('subservices.detail.consultantPrice')}
                      </Typography>
                      <Typography variant="body1" fontWeight={600} color="primary">
                        {formatPrice(internalSubservice.consultant_price)}
                      </Typography>
                    </Box>
                  </>
                ) : (
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                      {t('subservices.detail.price')}
                    </Typography>
                    <Typography variant="body1" fontWeight={600} color="primary">
                      {formatPrice(internalSubservice.price)}
                    </Typography>
                  </Box>
                )}

                <Box sx={{ gridColumn: { xs: '1', sm: '1 / -1' } }}>
                  <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                    {t('subservices.detail.description')}
                  </Typography>
                  <Typography variant="body1">{internalSubservice.description || t('subservices.detail.noDescription')}</Typography>
                </Box>

                {internalSubservice.description_ar && (
                  <Box sx={{ gridColumn: { xs: '1', sm: '1 / -1' } }}>
                    <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                      {t('subservices.detail.descriptionAr')}
                    </Typography>
                    <Typography variant="body1" dir="rtl">
                      {internalSubservice.description_ar}
                    </Typography>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
        </Box>

        {/* Sidebar */}
        <Box>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                {t('subservices.detail.internalIcons')}
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 2,
                  justifyContent: 'center',
                  my: 2,
                }}
              >
                <Avatar
                  src={internalSubservice.icon || undefined}
                  variant="rounded"
                  sx={{ width: 120, height: 120 }}
                >
                  <ImageIcon sx={{ fontSize: 60 }} />
                </Avatar>
                {internalSubservice.icon_gallery &&
                  internalSubservice.icon_gallery.map((url: string, idx: number) => (
                    <Avatar
                      key={idx}
                      src={url || undefined}
                      variant="rounded"
                      sx={{ width: 120, height: 120 }}
                    >
                      <ImageIcon sx={{ fontSize: 60 }} />
                    </Avatar>
                  ))}
              </Box>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                {t('subservices.detail.quickActions')}
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                <Button
                  variant="outlined"
                  startIcon={<EditIcon />}
                  fullWidth
                  onClick={() => navigate(`/internal-subservices/${internalSubservice.id}/edit`)}
                >
                  {t('subservices.buttons.editInternal')}
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteIcon />}
                  fullWidth
                  onClick={() => setDeleteDialog(true)}
                >
                  {t('subservices.buttons.deleteInternal')}
                </Button>
                {internalSubservice.parent_id && (
                  <Button
                    variant="outlined"
                    fullWidth
                    onClick={() => navigate(`/subservices/${internalSubservice.parent_id}`)}
                  >
                    {t('subservices.buttons.viewParentSubservice')}
                  </Button>
                )}
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>

      <ConfirmDialog
        open={deleteDialog}
        onClose={() => setDeleteDialog(false)}
        onConfirm={handleDelete}
        title={t('subservices.dialog.deleteInternalTitle')}
        message={t('subservices.dialog.deleteMessage', { name: internalSubservice?.name })}
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        confirmColor="error"
        loading={isDeleting}
      />
    </Box>
  );
};

export default InternalSubserviceDetailPage;

